import Button from '@rio-cloud/rio-uikit/Button';
import { getTrackingAttributes } from '@rio-cloud/rio-uikit/googleAnalyticsUtils';
import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import { isEmpty, propOr, values } from 'ramda';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { type Driver, DriverStatus, SidebarContentId } from '../../../../../../types';
import { ArchiveDriverDialog } from '../../../../dialogs/components/ArchiveDriverDialog';
import { DeleteDriverDialog } from '../../../../dialogs/components/DeleteDriverDialog';
import { DeleteIdentificationsConfirmDialog } from '../../../../dialogs/components/DeleteIdentificationsConfirmDialog';
import { DiscardDialog } from '../../../../dialogs/components/DiscardDialog';
import { PROP_FIRST_NAME, PROP_LAST_NAME } from '../../../../schema';
import { formatWithSpaces, SIDEBAR_BREAKPOINT_FLY } from '../../../../utils';
import { Footer } from '../../footer/Footer';
import { DriverGroupsFormContainer } from '../../tabs/groups/containers/DriverGroupsFormContainer';
import { DriverInfoFormContainer } from '../../tabs/info/containers/DriverInfoFormContainer';
import { AssignUserFormContainer } from '../../tabs/user/containers/AssignUserFormContainer';
import type { SidebarPropertiesFromDispatch, SidebarPropertiesFromState } from '../containers/SidebarContainer';

const SIDEBAR_DEFAULT_WIDTH = 420;
const SIDEBAR_MIN_WIDTH = 360;
const SIDEBAR_MAX_WIDTH = 720;

const hasErrors = (formErrors: { [p: string]: string | undefined }) => !isEmpty(values(formErrors).filter(Boolean));

type SidebarProps = SidebarPropertiesFromState & SidebarPropertiesFromDispatch;

export const DriverSidebar = (props: SidebarProps) => {
    const {
        hasChanges,
        formErrors,
        selectedDriver,
        selectedDriverWithoutEdits,
        showUnsavedChangesDialog,
        contentId,
        tagsToCreate,
        tagsToRemove,
        users,
        tagsToAdd,
        deleteDriver,
        handleKeepEditing,
        saveSelectedDriver,
        toggleDeleteUserDialog,
        setUsersToDelete,
    } = props;
    const [showDeleteDriverDialog, setShowDeleteDriverDialog] = useState<boolean>(false);
    const [showArchiveDriverDialog, setShowArchiveDriverDialog] = useState<boolean>(false);
    const [showDeleteIdentificationsDialog, setShowDeleteIdentificationsDialog] = useState<boolean>(false);
    const [pendingStatusAction, setPendingStatusAction] = useState<DriverStatus | null>(null);

    const handleTabChange = (tabKey: string) => {
        if (tabKey === props.contentId) {
            return;
        }

        if (tabKey === SidebarContentId.INFO) {
            props.handleTabChange(SidebarContentId.INFO);
        } else if (tabKey === SidebarContentId.USER) {
            props.handleTabChange(SidebarContentId.USER);
        } else if (tabKey === SidebarContentId.GROUP) {
            props.handleTabChange(SidebarContentId.GROUP);
        }
    };

    const handleConfirmDiscardAndChangeTabIfRequired = () => {
        if (props.nextTab !== null) {
            props.confirmDiscardAndChangeTab();
        } else {
            props.handleDiscardClose();
        }
    };
    const persistDriver = (nextStatus?: DriverStatus) => {
        if (selectedDriver === null) {
            return;
        }

        const driverToSave = trimDriver({
            ...selectedDriver,
            status: nextStatus ?? selectedDriver.status,
        });

        saveSelectedDriver(
            driverToSave,
            tagsToCreate,
            tagsToAdd,
            tagsToRemove,
            props.newIdentification,
            props.identificationIdsPendingForDeletion
        );

        const willBeArchived =
            driverToSave.status === DriverStatus.ARCHIVED &&
            selectedDriverWithoutEdits?.status !== DriverStatus.ARCHIVED &&
            selectedDriver.subject !== undefined;

        if (willBeArchived) {
            const usersToDelete = users.filter(user => user.subject === selectedDriver.subject);
            setUsersToDelete(usersToDelete);
            toggleDeleteUserDialog(true);
        }
    };

    const isSaveButtonEnabled = hasChanges && !hasErrors(formErrors) && !props.inviteUserRequested;
    const isStatusActionDisabled =
        selectedDriver === null || hasErrors(formErrors) || props.inviteUserRequested || props.fetchDriverInProgress;

    const saveWithOptionalStatusAction = (nextStatus?: DriverStatus) => {
        if (props.identificationIdsPendingForDeletion.size > 0) {
            setPendingStatusAction(nextStatus ?? null);
            setShowDeleteIdentificationsDialog(true);
            return;
        }

        persistDriver(nextStatus);
    };

    const handleSave = () => {
        if (!isSaveButtonEnabled) {
            return;
        }

        saveWithOptionalStatusAction();
    };

    const handleStatusActionClick = () => {
        if (isStatusActionDisabled || selectedDriver === null) {
            return;
        }

        if (selectedDriver.status === DriverStatus.ACTIVE) {
            setShowArchiveDriverDialog(true);
            return;
        }

        saveWithOptionalStatusAction(DriverStatus.ACTIVE);
    };

    const handleCancelDeleteIdentifications = () => {
        setShowDeleteIdentificationsDialog(false);
        setPendingStatusAction(null);
    };
    const handleConfirmDeleteIdentifications = () => {
        setShowDeleteIdentificationsDialog(false);
        persistDriver(pendingStatusAction ?? undefined);
        setPendingStatusAction(null);
    };

    const trimDriver = (driver: Driver): Driver => ({
        ...driver,
        firstName: driver.firstName?.trim(),
        lastName: driver.lastName?.trim(),
    });

    const handleDelete = () => {
        if (selectedDriver?.subject !== undefined) {
            const usersToDelete = users.filter(user => user.subject === selectedDriver.subject);
            setUsersToDelete(usersToDelete);
        }
        setShowDeleteDriverDialog(true);
    };

    const handleAbortDriverDeletion = () => {
        setUsersToDelete([]);
        setShowDeleteDriverDialog(false);
    };

    const handleAbortArchiveDriver = () => {
        setShowArchiveDriverDialog(false);
    };

    const handleConfirmArchiveDriver = () => {
        setShowArchiveDriverDialog(false);
        saveWithOptionalStatusAction(DriverStatus.ARCHIVED);
    };

    const handleSidebarPillKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, nextContentId: SidebarContentId) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }

        event.preventDefault();
        handleTabChange(nextContentId);
    };

    const handleConfirmDeleteDriver = () => {
        if (selectedDriver !== null) {
            if (selectedDriver?.subject) {
                toggleDeleteUserDialog(true);
            }
            deleteDriver(selectedDriver);
        }
        setShowDeleteDriverDialog(false);
    };

    const renderTabs = () => {
        return (
            <>
                <ul
                    aria-label='Driver sidebar navigation'
                    className='nav nav-pills nav-pills-filled nav-pills-filled-condensed nav-justified shadow-subtle margin-bottom-20'
                >
                    <li className={contentId === SidebarContentId.INFO ? 'active' : ''} role='presentation'>
                        <div
                            aria-selected={contentId === SidebarContentId.INFO}
                            onClick={() => handleTabChange(SidebarContentId.INFO)}
                            onKeyDown={event => handleSidebarPillKeyDown(event, SidebarContentId.INFO)}
                            role='tab'
                            tabIndex={0}
                            {...getTrackingAttributes({
                                trigger: 'click',
                                category: 'tab autoTracking',
                                action: 'tab click',
                                label: 'drivers.sidebar.tab.info',
                            })}
                        >
                            <FormattedMessage id='intl-msg:sidebarDriverInfo' />
                        </div>
                    </li>
                    <li className={contentId === SidebarContentId.GROUP ? 'active' : ''} role='presentation'>
                        <div
                            aria-selected={contentId === SidebarContentId.GROUP}
                            onClick={() => handleTabChange(SidebarContentId.GROUP)}
                            onKeyDown={event => handleSidebarPillKeyDown(event, SidebarContentId.GROUP)}
                            role='tab'
                            tabIndex={0}
                            {...getTrackingAttributes({
                                trigger: 'click',
                                category: 'tab autoTracking',
                                action: 'tab click',
                                label: 'drivers.sidebar.tab.group',
                            })}
                        >
                            <FormattedMessage id='intl-msg:sidebarDriverGroups' />
                        </div>
                    </li>
                    <li className={contentId === SidebarContentId.USER ? 'active' : ''} role='presentation'>
                        <div
                            aria-selected={contentId === SidebarContentId.USER}
                            onClick={() => handleTabChange(SidebarContentId.USER)}
                            onKeyDown={event => handleSidebarPillKeyDown(event, SidebarContentId.USER)}
                            role='tab'
                            tabIndex={0}
                            {...getTrackingAttributes({
                                trigger: 'click',
                                category: 'tab autoTracking',
                                action: 'tab click',
                                label: 'drivers.sidebar.tab.user',
                            })}
                        >
                            <FormattedMessage id='intl-msg:sidebarDriverUser' />
                        </div>
                    </li>
                </ul>
                <div className='padding-top-15'>{renderTab(contentId)}</div>
            </>
        );
    };

    const renderTab = (tabName: string) => {
        switch (tabName) {
            case 'user':
                return <AssignUserFormContainer />;
            case 'group':
                return <DriverGroupsFormContainer />;
            // biome-ignore lint/complexity/noUselessSwitchCase: suppress out of convenience during migration to biome
            case 'info':
            default:
                return <DriverInfoFormContainer />;
        }
    };

    // As lastName is also the driver card and may contain multiple spaces
    const lastName = formatWithSpaces(propOr('', PROP_LAST_NAME)(selectedDriver));
    const title = `${propOr('', PROP_FIRST_NAME)(selectedDriver)} ${lastName}`;

    const mapIdentificationIdToValue = (identificationId: string): string => {
        return selectedDriver?.identifications?.find(identification => identification.id === identificationId)
            ?.value as string;
    };
    const identificationsToBeDeleted = Array.from(props.identificationIdsPendingForDeletion).map(identificationIds =>
        mapIdentificationIdToValue(identificationIds)
    );

    return (
        <React.Fragment>
            {showUnsavedChangesDialog && (
                <DiscardDialog
                    onConfirmDiscard={handleConfirmDiscardAndChangeTabIfRequired}
                    onKeepEditing={handleKeepEditing}
                />
            )}
            {showDeleteDriverDialog && (
                <DeleteDriverDialog
                    driver={selectedDriver}
                    onConfirmDeleteDriver={handleConfirmDeleteDriver}
                    onCancelDeleteDriver={handleAbortDriverDeletion}
                />
            )}
            {showArchiveDriverDialog && (
                <ArchiveDriverDialog
                    driver={selectedDriver}
                    onConfirmArchiveDriver={handleConfirmArchiveDriver}
                    onCancelArchiveDriver={handleAbortArchiveDriver}
                />
            )}
            <DeleteIdentificationsConfirmDialog
                show={showDeleteIdentificationsDialog}
                identificationsToBeDeleted={identificationsToBeDeleted}
                handleConfirmDeleteIdentifications={handleConfirmDeleteIdentifications}
                handleCancelDeleteIdentifications={handleCancelDeleteIdentifications}
                tenant={props.tenant}
            />
            <Sidebar
                title={title}
                closed={false}
                position={Sidebar.RIGHT}
                onClose={props.handleCancel}
                disableClose
                footer={
                    <Footer
                        currentDriverStatus={selectedDriver?.status}
                        isSaveButtonEnabled={isSaveButtonEnabled}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        onStatusActionClick={handleStatusActionClick}
                        statusActionDisabled={isStatusActionDisabled}
                        data-testid='sidebarFooter'
                    />
                }
                headerButtons={
                    <Button
                        aria-label='Close'
                        autoTrackingKey='drivers.sidebar.close'
                        bsStyle={Button.MUTED}
                        iconName='icon-remove'
                        iconOnly
                        onClick={props.handleCancel}
                    />
                }
                resizable={true}
                minWidth={SIDEBAR_MIN_WIDTH}
                maxWidth={SIDEBAR_MAX_WIDTH}
                width={SIDEBAR_DEFAULT_WIDTH}
                switchModeBreakpoint={SIDEBAR_BREAKPOINT_FLY}
                titleClassName={'padding-left-10'}
                bodyClassName={'padding-20'}
                hasBackdrop={hasChanges}
                onBackdropClick={props.handleCancel}
            >
                {renderTabs()}
            </Sidebar>
        </React.Fragment>
    );
};
